import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const PriceReviewRulesPage = () => {
  const { currentOrgId, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');

  const [form, setForm] = useState({
    pattern: '',
    target_sku: '',
    vendor_account_ref: '',
    priority: 10,
    enabled: true
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setError('');
    if (!currentOrgId) return;
    setLoading(true);
    try {
      let qb = supabase
        .from('epos_match_rules')
        .select('id, pattern, pattern_type, case_sensitive, exact_match, vendor_account_ref, target_sku, priority, confidence_threshold, enabled, created_at, updated_at, created_by')
        .eq('org_id', currentOrgId)
        .order('updated_at', { ascending: false })
        .limit(200);
      const { data, error } = await qb;
      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load rules');
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [currentOrgId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r =>
      (r.pattern || '').toLowerCase().includes(term) ||
      (r.target_sku || '').toLowerCase().includes(term) ||
      (r.vendor_account_ref || '').toLowerCase().includes(term)
    );
  }, [rows, q]);

  const onCreate = async () => {
    if (!currentOrgId || !user?.id) return;
    const pattern = normalize(form.pattern);
    const target = (form.target_sku || '').trim();
    if (!pattern || !target) {
      alert('Pattern and target SKU are required');
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('epos_match_rules')
        .insert({
          org_id: currentOrgId,
          vendor_account_ref: form.vendor_account_ref?.trim() || null,
          pattern,
          pattern_type: 'exact',
          case_sensitive: false,
          exact_match: true,
          confidence_threshold: 1.0,
          priority: Number(form.priority) || 10,
          target_sku: target,
          created_by: user.id,
          enabled: !!form.enabled
        })
        .select('id, pattern, vendor_account_ref, target_sku, priority, enabled, updated_at')
        .single();
      if (error) throw error;
      setRows(prev => [data, ...prev]);
      setForm({ pattern: '', target_sku: '', vendor_account_ref: '', priority: 10, enabled: true });
    } catch (e) { alert(e?.message || 'Create failed'); }
    finally { setSaving(false); }
  };

  const onToggle = async (rule) => {
    try {
      const { error } = await supabase
        .from('epos_match_rules')
        .update({ enabled: !rule.enabled })
        .eq('id', rule.id)
        .eq('org_id', currentOrgId);
      if (error) throw error;
      setRows(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r));
    } catch (e) { alert(e?.message || 'Toggle failed'); }
  };

  const onDelete = async (rule) => {
    if (!confirm('Delete this rule?')) return;
    try {
      const { error } = await supabase
        .from('epos_match_rules')
        .delete()
        .eq('id', rule.id)
        .eq('org_id', currentOrgId);
      if (error) throw error;
      setRows(prev => prev.filter(r => r.id !== rule.id));
    } catch (e) { alert(e?.message || 'Delete failed'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">EPOS Match Rules</h1>
              <p className="text-sm text-muted-foreground">Link invoice line descriptions to EPOS products (auto-apply on review).</p>
            </div>
            <Button variant="outline" onClick={load} iconName="RefreshCw">Refresh</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Search"
              placeholder="Pattern, SKU, or vendor account ref…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />

            <div className="bg-card border border-border rounded-lg p-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Pattern (normalized)" placeholder="e.g. ground rice 5kg" value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} />
                <Input label="Target SKU" placeholder="e.g. 5013531780353" value={form.target_sku} onChange={(e) => setForm({ ...form, target_sku: e.target.value })} />
                <Input label="Vendor Account Ref (optional)" placeholder="e.g. NATCO" value={form.vendor_account_ref} onChange={(e) => setForm({ ...form, vendor_account_ref: e.target.value })} />
                <Input label="Priority" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <label className="text-sm flex items-center gap-2">
                  <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                  Enabled
                </label>
                <Button variant="default" onClick={onCreate} loading={saving} iconName="Plus" iconPosition="left">New Rule</Button>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg">
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-muted text-muted-foreground">
                    <Th>Pattern</Th>
                    <Th>Vendor</Th>
                    <Th>Target SKU</Th>
                    <Th className="text-right">Priority</Th>
                    <Th>Status</Th>
                    <Th>Updated</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td className="p-3" colSpan={7}>Loading…</td></tr>
                  ) : error ? (
                    <tr><td className="p-3 text-destructive" colSpan={7}>{error}</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td className="p-3 text-muted-foreground" colSpan={7}>No rules</td></tr>
                  ) : (
                    filtered.map(r => (
                      <tr key={r.id} className="border-t border-border">
                        <Td>{r.pattern}</Td>
                        <Td mono>{r.vendor_account_ref || ''}</Td>
                        <Td mono>{r.target_sku}</Td>
                        <Td num>{r.priority != null ? Number(r.priority) : ''}</Td>
                        <Td>
                          <span className={`text-xs px-2 py-1 rounded border ${r.enabled ? 'text-success border-success/40' : 'text-muted-foreground border-border'}`}>{r.enabled ? 'Enabled' : 'Disabled'}</span>
                        </Td>
                        <Td>{fmtDateTime(r.updated_at)}</Td>
                        <Td num>
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => onToggle(r)}>{r.enabled ? 'Disable' : 'Enable'}</Button>
                            <Button size="sm" variant="destructive" onClick={() => onDelete(r)}>Delete</Button>
                          </div>
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`text-left px-3 py-2 border-b border-border sticky top-0 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, num = false }) => (
  <td className={`px-3 py-2 align-top ${mono ? 'font-mono text-xs' : ''} ${num ? 'text-right' : ''}`}>{children}</td>
);

function fmtDateTime(iso) { try { const d = new Date(iso); if (Number.isNaN(d.getTime())) return iso || ''; return d.toLocaleString('en-GB'); } catch { return iso || ''; } }
function normalize(s) { try { return String(s||'').toLowerCase().replace(/\s+/g,' ').replace(/[^a-z0-9%&()\/\-., ]/g,'').trim(); } catch { return ''; } }

export default PriceReviewRulesPage;

